import { z } from 'zod';
import { objectIdSchema } from './common.js';
/**
 * Long-lived tokens for automations.
 *
 * A session's access token lives minutes and its refresh token rotates on every
 * use — correct for a browser, unusable for a phone automation that fires once
 * a day. Miss one rotation and the automation dies silently. These tokens do not
 * rotate and do not expire unless told to.
 *
 * That longevity is exactly why they are scoped. A token taped into a task on a
 * phone is far more exposed than a session, so it should be able to do one
 * thing. Scopes are a list from the start: widening the set later means adding
 * an enum value and a guard, never reshaping stored tokens.
 */
export const apiTokenScopeSchema = z.enum([
    'activity:read',
    'activity:write',
    'weight:read',
    'weight:write',
    'diary:read',
]);
export const API_TOKEN_SCOPES = apiTokenScopeSchema.options;
/** Human-readable purpose of each scope, for the UI. */
export const API_TOKEN_SCOPE_LABELS = {
    'activity:read': 'Aktivität lesen',
    'activity:write': 'Aktivität eintragen',
    'weight:read': 'Gewicht lesen',
    'weight:write': 'Gewicht eintragen',
    'diary:read': 'Tagebuch lesen',
};
export const createApiTokenSchema = z.object({
    /** What it is for, so a stale token can be recognised months later. */
    name: z.string().trim().min(1).max(80),
    scopes: z.array(apiTokenScopeSchema).min(1).max(20),
    /** Days until it stops working; omit for a token that does not expire. */
    expiresInDays: z.number().int().min(1).max(3650).nullable().optional(),
});
export const apiTokenSchema = z.object({
    id: objectIdSchema,
    name: z.string(),
    scopes: z.array(apiTokenScopeSchema),
    /** First characters of the token, to tell two entries apart in a list. */
    preview: z.string(),
    createdAt: z.string(),
    /** Null until the token is first used; the signal for spotting a dead one. */
    lastUsedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
});
/**
 * The creation response, and the only time the token itself is ever returned.
 * Only its hash is stored, so a lost token cannot be recovered — only replaced.
 */
export const createdApiTokenSchema = z.object({
    token: apiTokenSchema,
    plaintext: z.string(),
});
//# sourceMappingURL=apiToken.js.map